import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form } from 'react-final-form';
import { matchPath } from 'react-router-dom';

import { doScrollToErrorMessage } from '~/utils';
import { appointmentDraftSelectors } from '~/api';
import {
  QuestionsSections,
  ADDITIONAL_INFORMATION,
  HAIR_SKIN_EYES,
  SIZES_AND_AGE,
  apiCallFinished,
  errorMessagesActions,
  questionHelpers,
  withRequiredPrecondition,
  warningModalSelectors,
} from '~/components';
import { envConfigSelectors } from '~/components/configuration/configuration.selectors';
import { setEditMode } from '../../edit-appointment.actions';

import { EditButtonsWrapper } from '../../components/edit-buttons-wrapper';

import styles from '../../../../about-you/about-you-form/AboutYouForm.scss';
import { triggerEditPageSaveEditsAnalytics } from '~/analytics/analytics.listener';

// Custom validation function to replace jl-design-system validate
const validateQuestions = (questions, answers) => {
  if (!questions || !questions.RETRIEVED_QUESTIONS) return {};

  const errors = {};

  questions.RETRIEVED_QUESTIONS.forEach((question) => {
    const questionAnswer = answers?.[question.reduxFieldConfig.name];
    const validators = question.reduxFieldConfig.validate || [];

    // Ensure validators is an array before iterating
    if (!Array.isArray(validators)) {
      return;
    }

    for (const validator of validators) {
      // Ensure validator is a function before calling
      if (typeof validator !== 'function') {
        continue;
      }
      const error = validator(questionAnswer);
      if (error) {
        errors[question.reduxFieldConfig.name] = error;
        break;
      }
    }
  });

  return errors;
};

export class EditQuestionAnswers extends PureComponent {
  componentDidMount() {
    const { dispatchSetEditMode } = this.props;
    this.preloadData();
    dispatchSetEditMode(true);
    window.scrollTo(0, 0);
  }

  componentDidUpdate(prevprevProps) {
    const { errorMessages } = this.props;
    const canScrollToErrorMessage = prevprevProps.errorMessages !== errorMessages;

    doScrollToErrorMessage(canScrollToErrorMessage); // Validate condition to scroll to error message
  }

  preloadData = () => {
    const { preloadData, dispatchApiCallFinished } = this.props;

    dispatchApiCallFinished(false);
    preloadData().then(() => {
      dispatchApiCallFinished(true);
    });
  };

  submitHandler = (values) => {
    const {
      questions,
      history,

      appointment,
      appointmentTypeId,
      addressQuestionId,
      address,
      onSaveDataHandler,
      dispatchApiCallFinished,
    } = this.props;
    const {
      location: { pathname },
    } = history;

    const addressAnswer = appointment.answers.find(
      (answer) => answer.questionId === addressQuestionId,
    );

    const appointmentAnswers = [
      ...questionHelpers.getFormAnswers(questions, values, address, appointmentTypeId),
      addressAnswer,
    ].filter(Boolean);

    dispatchApiCallFinished(false);
    onSaveDataHandler(appointmentAnswers).then(() => {
      dispatchApiCallFinished(true);
      triggerEditPageSaveEditsAnalytics('questions');
      const match = matchPath(pathname, { path: '/:appointmentId([0-9]+)' }) || { params: {} };
      history.pushWithToken(
        match.params.appointmentId ? `/${match.params.appointmentId}` : '/review',
      );
    });
  };

  validateForm = (values) => {
    const { questions } = this.props;
    const questionsSectionErrors = validateQuestions(questions, values.questionsSection);

    return Object.keys(questionsSectionErrors).length > 0
      ? { questionsSection: questionsSectionErrors }
      : {};
  };

  render() {
    const {
      questions,
      sectionDataTest,
      match: {
        params: { section },
      },
      history,
      bookingId,
      initialFormState,
      dispatchSetEditMode,
      editMode,
      questionsSectionsConfig,
      location,
      showWarningModal,
      fittingTypes,
      initialValues,
    } = this.props;
    const isQuestionsRetrieved = questions && Object.keys(questions).length > 0;
    const isFittingTypes = Object.values(fittingTypes).filter((e) => e).length > 0;
    let filteredQuestions = { ...questions };
    if (isFittingTypes && isQuestionsRetrieved) {
      filteredQuestions.RETRIEVED_QUESTIONS = [
        ...questions.RETRIEVED_QUESTIONS.filter(({ title }) => !title.includes('Child')),
      ];
    }
    return (
      isQuestionsRetrieved && (
        <Form
          onSubmit={this.submitHandler}
          initialValues={initialValues}
          validate={this.validateForm}
          keepDirtyOnReinitialize
          render={({ handleSubmit, values }) => {
            const formState = values?.questionsSection || {};

            return (
              <form className={styles.AboutYouForm} onSubmit={handleSubmit}>
                <div className={styles.formWrapper}>
                  <QuestionsSections
                    formSectionName="questionsSection"
                    sectionKey={section}
                    questions={filteredQuestions}
                    questionsSectionsConfig={questionsSectionsConfig}
                    dataTest={sectionDataTest}
                    editMode={editMode}
                  />
                </div>
                <EditButtonsWrapper
                  history={history}
                  location={location}
                  showWarningModal={showWarningModal}
                  bookingId={bookingId}
                  temporaryDraftAppointment={initialFormState}
                  state={formState}
                  setEditMode={dispatchSetEditMode}
                  saveButtonText="Save"
                  saveButtonExtraProps={{
                    type: 'submit',
                  }}
                />
              </form>
            );
          }}
        />
      )
    );
  }
}

export const transformAnswers = (questions, answers) => {
  if (!answers) {
    return {};
  }

  const allQuestions = Object.values(questions).reduce((output, categoryQuestions) => {
    const concatenatedQuestions = output.concat(categoryQuestions);
    return concatenatedQuestions.reduce(
      (acc, curr) =>
        curr.children && curr.children.length ? [...acc, curr, ...curr.children] : [...acc, curr],
      [],
    );
  }, []);

  return answers.reduce((nextAnswers, answer) => {
    const question = allQuestions.find((el) => el.id === answer.questionId);

    return question
      ? {
          ...nextAnswers,
          [question.reduxFieldConfig.name]: question.restoreAnswer({
            question,
            answers: answer.answers,
          }),
        }
      : nextAnswers;
  }, {});
};

export const mapStateToProps = (state) => {
  const {
    questions: { questions },
    services: {
      config: { questionsSectionsConfig, AboutYouForm: aboutYouFormConfig },
    },
    appointment: {
      appointment,
      draft: {
        id: bookingId,
        appointmentType: { id: appointmentTypeId },
        fittingTypes,
      },
    },
    editAppointment: { editMode },
    envConfig,
    router: { location },
    messages: {
      app: { errorMessages },
    },
  } = state;
  const warningModalState = warningModalSelectors.selectState(state);
  const initialValues = {
    questionsSection: transformAnswers(questions, appointment && appointment.answers),
  };

  return {
    editMode,
    questions,
    fittingTypes,
    initialValues,
    questionsSectionsConfig,
    bookingId,
    ...(aboutYouFormConfig || {}),
    initialFormState: initialValues.questionsSection,
    envConfig,
    appointment,
    location,
    errorMessages: errorMessages || [],
    showWarningModal: !!warningModalState.isOpen,
    appointmentTypeId,
    addressQuestionId: envConfigSelectors.selectAppointmentTypeConfig(envConfig, appointmentTypeId)
      .addressQuestionId,
    address: appointmentDraftSelectors.selectAddress(state),
  };
};

export const mapDispatchToProps = {
  dispatchApiCallFinished: apiCallFinished,
  dispatchPushSectionErrorToErrorMessage: errorMessagesActions.pushSectionErrorToErrorMessage,
  dispatchSetEditMode: setEditMode,
};

export const ConnectedEditQuestionAnswers = withRequiredPrecondition(
  connect(mapStateToProps, mapDispatchToProps)(EditQuestionAnswers),
);

export const ConnectedEditQuestionAnswersForManageAppointment = connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditQuestionAnswers);

EditQuestionAnswers.propTypes = {
  bookingId: PropTypes.string,
  questions: PropTypes.shape({
    [SIZES_AND_AGE]: PropTypes.arrayOf(PropTypes.shape({})),
    [HAIR_SKIN_EYES]: PropTypes.arrayOf(PropTypes.shape({})),
    [ADDITIONAL_INFORMATION]: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  questionsSectionsConfig: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  initialFormState: PropTypes.shape({}).isRequired,
  formState: PropTypes.shape({}).isRequired,
  sectionDataTest: PropTypes.string,
  history: PropTypes.shape({}).isRequired,
  envConfig: PropTypes.shape({}).isRequired,
  appointment: PropTypes.shape({}).isRequired,
  editMode: PropTypes.bool.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      section: PropTypes.string,
    }).isRequired,
  }).isRequired,
  onSaveDataHandler: PropTypes.func.isRequired,
  preloadData: PropTypes.func.isRequired,
  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchSetEditMode: PropTypes.func.isRequired,
  dispatchPushSectionErrorToErrorMessage: PropTypes.func.isRequired,
  showWarningModal: PropTypes.bool.isRequired,
  location: PropTypes.shape({}).isRequired,
  appointmentTypeId: PropTypes.string.isRequired,
  addressQuestionId: PropTypes.string,
  address: PropTypes.shape({}),
  errorMessages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  initialValues: PropTypes.shape({}).isRequired,
  fittingTypes: PropTypes.shape({}),
};

EditQuestionAnswers.defaultProps = {
  sectionDataTest: null,
  bookingId: undefined,
  addressQuestionId: null,
  address: null,
};
